import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  buildCorpMasterLeadsListDefaultFilter,
  FilterNames,
  FilterRestService,
  FormStateBuilder,
  handleMultiSelectAllLogic,
  LEADS_LIST_TRANSIENT_QUERY_PARAMS,
  markLeadsListPageReloadHandled,
  OfficeAccessService,
  peekLeadsListPageReload,
  resolveCorpMasterSalesPersonIds,
  resolveCorpOfficeSelection,
  resolveLeadsListFields,
  rewriteFilterQueryWithoutHiddenLeads,
  toCorpOfficeFilterOptions,
} from '@gh/builder-shared';
import { BuilderPermission, compareStageItemCodes, LeadsDatesFilter } from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  dictionaryItemFromDto,
  DictionaryService,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  globalDictionaries,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { AutocompleteLoadMoreEvent, Confirmation, DialogService, NotificationService, WaitingService, newFormControl } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridComponent,
  GridDefinitionFactory,
  GridLayout,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { sortByNumberField, sortByStringField } from '@gh/core-util';
import { combineLatest$, of$ } from '@gh/rx';
import { map$, takeUntil$, tap$ } from '@gh/rx/operators';
import { omit } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { LeadListBusinessService, UserDetailsShort } from '../../resources/lead-list-business.service';
import { Office } from '../../../builder-shared/switch-to/resources/office.model';
import { MasterZone } from '../../../builder-shared/switch-to/resources/master-zone.model';
import { LeadListItem } from '../../../builder-sales/leads/resources/lead.model';
import { LeadsListFilter } from '../../../builder-sales/leads/resources/lead-board.model';
import { focusSelectSearchInput, matchesSelectSearch } from '../../utils/lead-select-search';
import { BulkEditLeadsDialogComponent } from '../bulk-edit-leads-dialog/bulk-edit-leads-dialog.component';
import { BulkReassignLeadsDialogComponent } from '../bulk-reassign-leads-dialog/bulk-reassign-leads-dialog.component';
import { CopyArchiveLeadDialogComponent } from '../copy-archive-lead-dialog/copy-archive-lead-dialog.component';

@Component({
  standalone: false,
  selector: 'gh-lead-list-page',
  templateUrl: 'lead-list.component.html',
  styleUrls: ['lead-list.component.scss'],
})
export class LeadListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<LeadListItem>;
  gridDefinition: LayoutBasedGridDefinition;
  private leadsGridLayout: GridLayout;
  private lastListFieldsKey = '';
  form: FilterableFormState<any>;
  cursor = newGridPaginationCursor();
  notProceedingReasons: any[];
  stages: any[];
  salesFolderStatuses: any[];
  priorities: any[];
  referredByList: any[];
  offices: Office[] = [];
  salesPersons: UserDetailsShort[] = [];
  masterZoneList: MasterZone[] = [];
  officeSearch = '';
  salesPersonSearch = '';
  selectedLeads: Set<number> = new Set<number>();
  currentItems: LeadListItem[] = [];
  willNotComplete = 3;

  @ViewChild(GridComponent) grid: GridComponent;
  @ViewChild('officeSearchInput') officeSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('salesPersonSearchInput') salesPersonSearchInput?: ElementRef<HTMLInputElement>;

  /** Serialized normalized office ids — skips duplicate selectionChange / no-op office updates. */
  private lastNormalizedOfficeSelection = '';
  private lastOfficeSelection: string[] = [''];
  private lastSalesPersonSelection: string[] = [''];
  private officeLoadSeq = 0;
  private officeLoadSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  /** Date filter type options for Corporate/Master leads (like Contact Manager Dates). */
  leadsDateTypeOptions: { id: LeadsDatesFilter; nameKey: string }[] = [
    { id: LeadsDatesFilter.CreateDate, nameKey: 'lbl_leads_date_create' },
    { id: LeadsDatesFilter.LastContactDate, nameKey: 'lbl_leads_date_last_contact' },
  ];

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private dictionaryService: DictionaryService,
              private businessService: LeadListBusinessService,
              private router: AppRouter,
              private angRouter: Router,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private builderContext: BuilderUserContext,
              private dialogService: DialogService,
              private formStateBuilder: FormStateBuilder,
              private notificationService: NotificationService,
              private translation: TranslationService,
              private cdr: ChangeDetectorRef,
              private filterService: FilterRestService,
              private waiting: WaitingService,
              private officeAccessService: OfficeAccessService,
  ) {
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        ref: {
          minWidth: 110,
          valueGetter: (field) => {
            const id = field.data?.leadId || field.data?.contactId;
            return id ? `L-${id}` : '';
          },
          comparator: (a, b, nodeA, nodeB) => (Number(nodeA.data?.leadId || nodeA.data?.contactId) || 0) - (Number(nodeB.data?.leadId || nodeB.data?.contactId) || 0),
        },
        lastContactDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        createdDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        nextActivity: buildDateTimeColumn(DateTimeTypes.DateTime),
        meetingSfStatusId: {
          minWidth: 100,
          cellClassRules: {
            'active-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 1,
            'finalized-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 2,
            'deleted-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 3,
          },
          valueGetter: (field) => {
            let result = this.salesFolderStatuses.find(
              (status) => parseInt(status.itemCode) === field.data.meetingSfStatusId)?.localization;
            if (parseInt(field.data.meetingSfStatusId) === this.willNotComplete && field.data.notProceedingReason) {
              result += ` (${ this.notProceedingReasons.find((reason) => reason.id.toString() === field.data.notProceedingReason?.toString())?.name})`;
            }
            return result;
          },
        },
        meetingSfPriorityId: {
          valueGetter: (field) =>
            this.priorities.find((priority) => parseInt(priority.itemCode) === field.data.meetingSfPriorityId)?.localization,
        },
        meetingSfStageId: {
          valueGetter: (field) =>
            this.stages.find((stage) => parseInt(stage.itemCode) === field.data.meetingSfStageId)?.localization,
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.contactId),
      });
  }

  /** Multi-office: bulk reassign is hidden for a Salesperson in a membership office. */
  reassignRestricted = false;

  ngOnInit(): void {
    this.officeAccessService.isReassignRestricted(this.builderContext).subscribe((restricted) => {
      this.reassignRestricted = restricted;
      this.cdr.markForCheck();
    });
    let isInit = true;
    const {
      filter: initFilter,
      gridLayout,
      masterZones,
      offices,
      salesPersons,
      salesFolderStatuses,
    } = getRouteInitData(this.route);

    this.offices = offices || [];
    this.salesPersons = Array.isArray(salesPersons) ? salesPersons : (salesPersons?.id ? [salesPersons] : []);
    this.masterZoneList = masterZones || [];

    const masterZone = (initFilter.masterZone && typeof initFilter.masterZone === 'object' && initFilter.masterZone.id)
      ? initFilter.masterZone
      : (initFilter.masterZoneId
          ? (masterZones || []).find((item: MasterZone) => item.id === initFilter.masterZoneId) || (this.builderContext.masterView && this.builderContext.masterZone ? this.builderContext.masterZone : {id: '', name: 'All'})
          : (this.builderContext.masterView && this.builderContext.masterZone ? this.builderContext.masterZone : {id: '', name: 'All'}));

    combineLatest$([
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.referredBy),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons).pipe(
        map$((res) => res.map(dictionaryItemFromDto))),
    ]).subscribe(([stagesDict, priorities, referred, notProceedingReasonsList]) => {
      this.stages = [...stagesDict].sort((a, b) => compareStageItemCodes(a.itemCode, b.itemCode));
      this.priorities = sortByStringField(priorities, 'localization');
      this.referredByList = sortByNumberField(referred, 'itemCode');
      this.notProceedingReasons = notProceedingReasonsList;
    });

    this.leadsGridLayout = gridLayout;
    this.gridDefinition.bind(gridLayout);
    this.salesFolderStatuses = salesFolderStatuses;

    const pageReload = peekLeadsListPageReload();
    const defaultFilter = buildCorpMasterLeadsListDefaultFilter(this.builderContext);
    const salesPersonIds = pageReload
      ? defaultFilter.salesPersonId
      : resolveCorpMasterSalesPersonIds(initFilter.salesPersonId, this.salesPersons, this.builderContext);
    const officeId = this.builderContext.corporateView
      ? resolveCorpOfficeSelection(this.offices, pageReload ? defaultFilter.officeId : initFilter.officeId)
      : (pageReload
        ? defaultFilter.officeId
        : ((Array.isArray(initFilter.officeId) && initFilter.officeId.length > 0)
          ? initFilter.officeId
          : (initFilter.officeId ? [initFilter.officeId] : [''])));
    const formValue = pageReload ? { ...defaultFilter, officeId } : {
      ...initFilter,
      salesPersonId: salesPersonIds,
      officeId,
      masterZone,
      masterZoneId: this.builderContext.masterView && this.builderContext.masterZone?.id
        ? this.builderContext.masterZone.id
        : (initFilter.masterZoneId || masterZone?.id || ''),
      showSuppressed: false,
    };

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: formValue,
      children: {
        masterZoneId: { visible: this.builderContext.corporateView },
        masterZone: { visible: this.builderContext.corporateView },
        officeId: void 0,
        searchString: void 0,
        showInactive: void 0,
        showActive: void 0,
        showOverdue: void 0,
        showCompleted: void 0,
        salesPersonId: void 0,
        stage: void 0,
        priority: void 0,
        referredBy: void 0,
        fromDate: void 0,
        toDate: void 0,
        dateType: void 0,
        showSuppressed: void 0,
      },
    }));

    this.lastNormalizedOfficeSelection = LeadListComponent.serializeOfficeSelection(
      this.form.get('officeId').value as string[] | undefined,
    );
    this.lastOfficeSelection = Array.isArray(this.form.get('officeId').value) ? this.form.get('officeId').value : [''];
    this.lastSalesPersonSelection = Array.isArray(this.form.get('salesPersonId').value) ? this.form.get('salesPersonId').value : [''];

    // Loading initial salespersons if offices are pre-selected
    if (this.form.get('officeId').value?.length || this.form.get('masterZoneId').value) {
      this.reloadSalesPersons();
    }

    const onFilterChange = ([filter]: [any]) => {
      // Prevent API calls when date filter is incomplete (Type selected without From/To, or vice versa)
      const hasDateType = !!filter.dateType;
      const hasFromDate = !!filter.fromDate;
      const hasToDate = !!filter.toDate;
      const hasBothDates = hasFromDate && hasToDate;
      const hasAnyDate = hasFromDate || hasToDate;

      const isDateFilterComplete = (hasDateType && hasBothDates) || (!hasDateType && !hasAnyDate);

      if (!isDateFilterComplete) {
        return;
      }

      const fixedFilter = {
        ...omit(filter, 'stage', 'priority', 'salesPersonId', 'salesPerson', 'referredBy', 'office', 'officeId', 'masterZone', 'masterZoneId'),
      } as LeadsListFilter;

      if (filter.stage) {
        fixedFilter.stage = filter.stage;
      }
      if (filter.priority) {
        fixedFilter.priority = filter.priority;
      }
      if (filter.salesPersonId && filter.salesPersonId.length) {
        fixedFilter.salesPersonId = filter.salesPersonId.filter((id: string) => id !== '');
      }
      if (filter.referredBy) {
        fixedFilter.referredBy = filter.referredBy;
      }
      const officeIdsFiltered = filter.officeId && filter.officeId.length
        ? filter.officeId.filter((id: string) => id !== '')
        : [];
      if (officeIdsFiltered.length > 0) {
        fixedFilter.officeId = officeIdsFiltered;
      }
      // When in Master Zone View, always scope to the current Master Zone.
      // In Corporate View, if a specific Master Zone is selected (not "All"), scope to it.
      if (this.builderContext.masterView && this.builderContext.masterZone?.id) {
        fixedFilter.masterZoneId = this.builderContext.masterZone.id;
      } else if (filter.masterZone?.id && filter.masterZone.id !== '' && filter.masterZone.id !== 'All') {
        fixedFilter.masterZoneId = filter.masterZone.id;
      }
      if (filter.showSuppressed !== undefined) {
        fixedFilter.showSuppressed = filter.showSuppressed;
      }
      if (filter.dateType) {
        fixedFilter.dateType = filter.dateType;
      }
      if (filter.fromDate != null) {
        fixedFilter.fromDate = filter.fromDate;
      }
      if (filter.toDate != null) {
        fixedFilter.toDate = filter.toDate;
      }

      const originalDs = this.businessService.findAllAsDataSource(fixedFilter, this.leadsGridLayout);
      this.dataSource = {
        select: (query) => originalDs.select(query).pipe(
          tap$((res) => {
            this.currentItems = res.data;
            this.selectedLeads.clear();
            this.cdr.markForCheck();
          }),
        ),
      };

      if (!isInit) {
        const filterName = this.builderContext.corporateView ? FilterNames.leadsListCorp : FilterNames.leadsListMaster;
        const filterToSave = {
          masterZoneId: filter.masterZone?.id && filter.masterZone.id !== 'All' ? filter.masterZone.id : (filter.masterZoneId || ''),
          officeId: Array.isArray(filter.officeId) ? filter.officeId : (filter.officeId ? [filter.officeId] : ['']),
        };
        this.filterService.update(filterName, filterToSave).subscribe();
      }
      isInit = false;
    };

    const setupTracker = (): void => {
      const tracker = this.routeData
        .tracker(filterTracker(this.form))
        .withEffects(cursorTracker(this.cursor));
      tracker.listen(this.route);
      tracker.filterChanges.subscribe(onFilterChange);
    };

    if (pageReload) {
      markLeadsListPageReloadHandled();
      this.clearTransientQueryParams().then(() => {
        this.form.setFilter(formValue);
        setupTracker();
        if (this.form.get('officeId').value?.length || this.form.get('masterZoneId').value) {
          this.reloadSalesPersons(() => this.form.emitFilter());
        } else {
          this.form.emitFilter();
        }
      });
    } else {
      this.clearHiddenLeadsFromFilterQuery().then(() => setupTracker());
    }
  }

  ngOnDestroy(): void {
    this.officeLoadSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearHiddenLeadsFromFilterQuery(): Promise<boolean> {
    const rewritten = rewriteFilterQueryWithoutHiddenLeads(this.route.snapshot.queryParams['filter']);
    if (!rewritten) {
      return Promise.resolve(true);
    }
    return this.angRouter.navigate([], {
      relativeTo: this.route,
      queryParams: { filter: rewritten },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearTransientQueryParams(): Promise<boolean> {
    const cleared: Params = {};
    LEADS_LIST_TRANSIENT_QUERY_PARAMS.forEach((name) => {
      cleared[name] = null;
    });
    return this.angRouter.navigate([], {
      relativeTo: this.route,
      queryParams: cleared,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** True when any date filter (dateType, fromDate, toDate) has a value – show Reset button. */
  get isDatesResetVisible(): boolean {
    if (!this.form) {
      return false;
    }
    const dateType = this.form.get('dateType')?.value;
    const fromDate = this.form.get('fromDate')?.value;
    const toDate = this.form.get('toDate')?.value;
    return !!dateType || fromDate != null || toDate != null;
  }

  /** Reset date type and From/To (like Contact Manager Dates reset). */
  onResetDates(): void {
    this.form.get('dateType')?.setValue(null);
    this.form.get('fromDate')?.setValue(null);
    this.form.get('toDate')?.setValue(null);
    this.form.emitFilter();
  }

  onShowSuppressedChange(event: { checked: boolean }): void {
    this.form.get('showSuppressed').setValue(event.checked);
    this.form.emitFilter();
  }

  onEdit(lead: LeadListItem): void {
    if (!lead?.officeId) {
      return;
    }
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${ lead.officeId }/sales/leads/${ lead.contactId }`]));
    window.open(url, '_blank');
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onGridLayoutChange(layout: GridLayout): void {
    this.leadsGridLayout = layout;
    const fieldsKey = (resolveLeadsListFields(layout, this.form?.currentFilter || {}, layout.sort?.field) || [])
      .sort()
      .join('|');
    if (fieldsKey === this.lastListFieldsKey) {
      return;
    }
    this.lastListFieldsKey = fieldsKey;
    this.form.emitFilter();
  }

  /**
   * Loads salesperson options for the current Master Zone / Office selection.
   * When {@code onLoaded} is provided, it runs after the list is applied (used to emit the grid filter
   * so the API request sees office + salesperson state together — same timing as single-select filters).
   */
  reloadSalesPersons(onLoaded?: () => void): void {
    const officeIds = this.form.get('officeId').value;
    const masterZoneId = this.form.get('masterZoneId').value || (this.form.get('masterZone').value?.id !== 'All' ? this.form.get('masterZone').value?.id : null);

    this.businessService.findSalesPersonsByName({ from: 0, to: 1000 }, {
      masterZoneIds: masterZoneId ? [masterZoneId] : [],
      officeIds: Array.isArray(officeIds) ? officeIds.filter((id: string) => id !== '') : (officeIds && officeIds !== '' ? [officeIds] : []),
      showInactive: true,
    }).pipe(takeUntil$(this.destroy$)).subscribe(res => {
      this.salesPersons = [{id: '', name: 'All'}, ...res];
      onLoaded?.();
    });
  }

  onOfficeIdsSelected(): void {
    const control = this.form.get('officeId');
    const current = (control.value as string[]) || [];

    const values = handleMultiSelectAllLogic(current, this.lastOfficeSelection, this.offices);
    this.lastOfficeSelection = values;

    control.setValue(values, { emitEvent: false });

    const nextKey = LeadListComponent.serializeOfficeSelection(values);
    if (nextKey === this.lastNormalizedOfficeSelection) {
      return;
    }
    this.lastNormalizedOfficeSelection = nextKey;

    // Office drives which salespersons exist; reset to All and emit only after reload so the list request
    // does not briefly use the previous office's salesperson IDs (same pattern as Referred By: one coherent filter).
    this.form.get('salesPersonId').setValue([''], { emitEvent: false });
    this.lastSalesPersonSelection = [''];
    this.reloadSalesPersons(() => this.form.emitFilter());
  }

  private static serializeOfficeSelection(ids: string[] | undefined): string {
    return JSON.stringify((ids || []).slice().sort());
  }

  onSalesPersonIdsSelected(): void {
    const control = this.form.get('salesPersonId');
    const current = (control.value as string[]) || [];

    const values = handleMultiSelectAllLogic(current, this.lastSalesPersonSelection, this.salesPersons);
    this.lastSalesPersonSelection = values;

    control.setValue(values, { emitEvent: false });

    this.form.emitFilter();
  }

  get selectedSalesPersons() {
    const ids = this.form?.get('salesPersonId')?.value || [];
    if (!Array.isArray(ids)) {
      return this.salesPersons.filter(sp => sp.id === ids);
    }
    return this.salesPersons.filter(sp => ids.includes(sp.id));
  }

  get selectedOffices(): Office[] {
    const ids = this.form?.get('officeId')?.value || [];
    if (!Array.isArray(ids)) {
      return this.offices.filter((o) => o.id === ids);
    }
    return this.offices.filter((o) => ids.includes(o.id as string));
  }

  /** Full selection text for native tooltip on truncated Office / Salesperson triggers. */
  get selectedOfficeNamesTitle(): string {
    return this.selectedOffices
      .map((o) => (o.id === '' ? this.translation.get('lbl_all') : o.name))
      .join(', ');
  }

  get selectedSalesPersonNamesTitle(): string {
    return this.selectedSalesPersons
      .map((sp) => {
        const inactive = sp.isInactive ? ` (${this.translation.get('lbl_inactive')})` : '';
        return `${sp.name}${inactive}`;
      })
      .join(', ');
  }

  /** Same rule as House & Land list: only show Inactive when API sends explicit `active: false`. */
  isMasterZoneInactive(mz: MasterZone): boolean {
    return !!mz && Object.prototype.hasOwnProperty.call(mz, 'active') && !mz.active;
  }

  isOfficeDeactivated(office: any): boolean {
    return !!office && !!office.status && office.status.toUpperCase() === 'DEACTIVATED';
  }

  isOfficeOptionVisible(office: Office): boolean {
    return matchesSelectSearch(
      this.officeSearch,
      office.id === '' ? this.translation.get('lbl_all') : office.name,
    );
  }

  isSalesPersonOptionVisible(sp: UserDetailsShort): boolean {
    return matchesSelectSearch(this.salesPersonSearch, sp.name);
  }

  get hasNoMatchingOffices(): boolean {
    return !!this.officeSearch.trim() && !this.offices.some((office) => this.isOfficeOptionVisible(office));
  }

  get hasNoMatchingSalesPersons(): boolean {
    return !!this.salesPersonSearch.trim() && !this.salesPersons.some((sp) => this.isSalesPersonOptionVisible(sp));
  }

  onOfficeSelectOpened(opened: boolean): void {
    this.onSelectSearchOpened(opened, 'officeSearch', this.officeSearchInput);
  }

  onSalesPersonSelectOpened(opened: boolean): void {
    this.onSelectSearchOpened(opened, 'salesPersonSearch', this.salesPersonSearchInput);
  }

  onOfficeSearch(event: Event): void {
    this.officeSearch = (event.target as HTMLInputElement).value;
  }

  onSalesPersonSearch(event: Event): void {
    this.salesPersonSearch = (event.target as HTMLInputElement).value;
  }

  private onSelectSearchOpened(
    opened: boolean,
    field: 'officeSearch' | 'salesPersonSearch',
    input?: ElementRef<HTMLInputElement>,
  ): void {
    if (opened) {
      focusSelectSearchInput(input);
      return;
    }
    this[field] = '';
  }

  loadMasterZones(event: AutocompleteLoadMoreEvent): void {
    const query = (event.query.filter || '').toLowerCase();
    const allOption = { id: '', name: this.translation.get('lbl_all') } as MasterZone;
    event.load(of$(query
      ? this.masterZoneList.filter((masterZone: MasterZone) =>
          (masterZone.name || '').toLowerCase().includes(query))
      : [allOption, ...this.masterZoneList]));
  }

  onMasterZoneSelected(masterZone: MasterZone): void {
    if (!masterZone || masterZone.id !== this.form.getControl('masterZoneId').value) {
      const mzId = masterZone?.id || '';
      const seq = ++this.officeLoadSeq;
      this.form.getControl('masterZoneId').setValue(mzId);
      this.offices = [{ id: '', name: 'All' } as Office];
      this.form.getControl('officeId').setValue([''], { emitEvent: false });
      this.lastOfficeSelection = [''];
      this.lastNormalizedOfficeSelection = LeadListComponent.serializeOfficeSelection(['']);
      this.form.getControl('salesPersonId').setValue([''], { emitEvent: false });
      this.lastSalesPersonSelection = [''];
      this.salesPersons = [{id: '', name: 'All'}];
      this.form.emitFilter();
      const knownZoneIds = mzId
        ? undefined
        : (this.masterZoneList || [])
          .map((zone) => zone.id)
          .filter((id) => id != null && String(id) !== '');
      this.officeLoadSub?.unsubscribe();
      this.officeLoadSub = this.businessService.findOfficesForCorpLeadsFilter(mzId || undefined, knownZoneIds)
        .pipe(takeUntil$(this.destroy$))
        .subscribe({
        next: (res) => {
          if (seq !== this.officeLoadSeq) {
            return;
          }
          this.offices = toCorpOfficeFilterOptions(res);
          const officeIds = resolveCorpOfficeSelection(this.offices);
          this.form.getControl('officeId').setValue(officeIds, { emitEvent: false });
          this.lastOfficeSelection = officeIds;
          this.lastNormalizedOfficeSelection = LeadListComponent.serializeOfficeSelection(officeIds);
          const officeIsAll = officeIds.length === 1 && officeIds[0] === '';
          this.reloadSalesPersons(() => {
            if (!officeIsAll) {
              this.form.emitFilter();
            }
          });
        },
        error: (err) => {
          if (seq !== this.officeLoadSeq) {
            return;
          }
          this.notificationService.processAll(err);
        },
      });
    }
  }

  onDateFilterChange(): void {
    const fromDate = this.form.get('fromDate').value;
    const toDate = this.form.get('toDate').value;
    const dateType = this.form.get('dateType').value;

    if (dateType != null && dateType !== '' && fromDate != null && toDate != null) {
      this.onSearch();
    } else if (fromDate == null && toDate == null) {
      // Allow searching when dates are cleared to reset the filter
      this.onSearch();
    }
  }

  onGenericFilterChange(): void {
    this.onSearch();
  }

  goToAel(lead?: LeadListItem): void {
    if (lead && lead.officeId) {
      this.navigationService.goToOfficeAel(lead.officeId, {
        label: 'lbl_leads',
        entityType: 'LEAD',
        entityId: lead.contactId,
      });
      return;
    }
    this.navigationService.goToAel({
      label: 'lbl_leads',
      entityType: 'LEAD',
    });
  }

  isSelectionModeActive(): boolean {
    return this.selectedLeads.size > 0;
  }

  onToggleSelection(lead: LeadListItem): void {
    const cid = lead.contactId as any as number;
    if (this.selectedLeads.has(cid)) {
      this.selectedLeads.delete(cid);
    } else {
      if (this.selectedLeads.size >= 200) {
        this.notificationService.warning('msg_max_200_leads_selection_limit');
        return;
      }
      this.selectedLeads.add(cid);
    }
    this.cdr.markForCheck();
  }

  onToggleAllSelection(checked: boolean): void {
    if (checked) {
      this.currentItems.forEach((lead: LeadListItem) => {
        if (this.selectedLeads.size < 200) {
          this.selectedLeads.add(lead.contactId as any as number);
        }
      });
    } else {
      this.selectedLeads.clear();
    }
    this.cdr.markForCheck();
  }

  isLeadSelected(lead: LeadListItem): boolean {
    return this.selectedLeads.has(lead.contactId as any as number);
  }

  get allOnPageSelected(): boolean {
    return this.currentItems.length > 0 && this.currentItems.every((lead: LeadListItem) => this.selectedLeads.has(lead.contactId as any as number));
  }

  get isSelectedPartial(): boolean {
    return this.selectedLeads.size > 0 && !this.allOnPageSelected;
  }

  get selectedLeadsCountLabel(): string {
    return this.selectedLeads.size === 1 ? 'lbl_lead_selected' : 'lbl_leads_selected';
  }

  get hideLeadActionLabel(): string {
    return this.selectedLeads.size === 1 ? 'lbl_hide_lead' : 'lbl_hide_leads_bulk';
  }

  get unhideLeadActionLabel(): string {
    return this.selectedLeads.size === 1 ? 'lbl_unhide_lead' : 'lbl_unhide_leads_bulk';
  }

  onBulkHide(): void {
    if (this.selectedLeads.size === 0) {
      return;
    }

    const count = this.selectedLeads.size;
    const msgKey = count === 1
      ? 'msg_hide_lead_from_lists_confirmation'
      : 'msg_hide_leads_from_lists_confirmation';
    const msg = this.translation.get(msgKey);
    this.dialogService.confirmation({
      message: msg,
    }).subscribe(confirmed => {
      if (confirmed === Confirmation.Yes) {
        const leadsToHide = this.currentItems
          .filter((lead: LeadListItem) => this.selectedLeads.has(lead.contactId as any as number))
          .map((lead: LeadListItem) => ({ contactId: lead.contactId, salesFolderId: lead.activeSalesFolderId }));

        this.businessService.bulkSuppress(leadsToHide)
          .pipe(this.waiting.operator())
          .subscribe({
            next: () => {
              this.notificationService.success('msg_bulk_hide_leads_success');
              this.selectedLeads.clear();
              this.cdr.markForCheck();
              this.onSearch();
            },
            error: (err) => {
              this.notificationService.processAll(err);
            },
          });
      }
    });
  }

  onBulkUnhide(): void {
    if (this.selectedLeads.size === 0) {
      return;
    }

    const count = this.selectedLeads.size;
    const msg = this.translation.get('msg_unhide_x_leads_confirmation', { count: count.toString() });
    this.dialogService.confirmation({
      message: msg,
    }).subscribe(confirmed => {
      if (confirmed === Confirmation.Yes) {
        const leadsToUnhide = this.currentItems
          .filter((lead: LeadListItem) => this.selectedLeads.has(lead.contactId as any as number))
          .map((lead: LeadListItem) => ({ contactId: lead.contactId, salesFolderId: lead.activeSalesFolderId }));

        this.businessService.bulkUnsuppress(leadsToUnhide)
          .pipe(this.waiting.operator())
          .subscribe({
            next: () => {
              this.notificationService.success('msg_bulk_unhide_leads_success');
              this.selectedLeads.clear();
              this.cdr.markForCheck();
              this.onSearch();
            },
            error: (err) => {
              this.notificationService.processAll(err);
            },
          });
      }
    });
  }

  onBulkEditLeads(): void {
    if (this.selectedLeads.size === 0) {
      return;
    }

    const selectedList = this.currentItems.filter((lead: LeadListItem) =>
      this.selectedLeads.has(lead.contactId as any as number),
    );
    const COMPLETED_STATUS_ID = 2;
    const hasCompletedLeads = selectedList.some((lead) => lead.meetingSfStatusId === COMPLETED_STATUS_ID);
    if (hasCompletedLeads) {
      this.notificationService.warning('msg_bulk_edit_completed_leads_error');
      return;
    }

    this.dialogService.open(BulkEditLeadsDialogComponent, {
      data: {
        contactIds: Array.from(this.selectedLeads).map(id => id.toString()),
        reasons: this.notProceedingReasons,
        stages: this.stages,
        priorities: this.priorities,
      },
      width: '600px',
      disableClose: true,
    }).afterClosed().subscribe((success) => {
      if (success) {
        this.selectedLeads.clear();
        this.cdr.markForCheck();
        this.onSearch();
      }
    });
  }

  onBulkReassignLeads(): void {
    if (this.selectedLeads.size === 0 || this.reassignRestricted) {
      return;
    }

    const selectedList = this.currentItems.filter((lead: LeadListItem) => this.selectedLeads.has(lead.contactId as any as number));

    // UC-1: Block reassignment if any selected lead is Completed (meetingSfStatusId === 2)
    const COMPLETED_STATUS_ID = 2;
    const hasCompletedLeads = selectedList.some(lead => lead.meetingSfStatusId === COMPLETED_STATUS_ID);
    if (hasCompletedLeads) {
      this.notificationService.warning('msg_bulk_reassign_completed_leads_error');
      return;
    }

    const firstLead = selectedList[0];
    let availableOffices = this.offices.filter(o => o.id !== '');

    const openBulkReassignDialog = (offices: Office[]) => {
      let list = [...offices];
      if (list.length === 0) {
        list = [{ id: firstLead.officeId, name: firstLead.officeName as string } as Office];
      } else if (firstLead.officeId != null && !list.find(o => String(o.id) === String(firstLead.officeId))) {
        list.push({ id: firstLead.officeId, name: firstLead.officeName as string } as Office);
      }

      const sourceSalespersonIds = [...new Set(selectedList.map(lead => String(lead.salespersonId)))];

      const selectedMzId = this.form.getControl('masterZoneId')?.value;
      const masterZoneId = (selectedMzId && selectedMzId !== '') ? selectedMzId : undefined;

      this.dialogService.open(BulkReassignLeadsDialogComponent, {
        data: {
          leadIds: selectedList.map((lead) => String(lead.activeSalesFolderId)),
          sourceOfficeId: firstLead.officeId != null ? String(firstLead.officeId) : undefined,
          sourceSalespersonIds,
          masterZoneId,
          offices: list,
        },
        width: '520px',
        disableClose: true,
      }).afterClosed().subscribe((success) => {
        if (success) {
          this.selectedLeads.clear();
          this.cdr.markForCheck();
          this.onSearch();
        }
      });
    };

    // Corporate + Master Zone & Office “All”: sidebar has no real offices — load active offices from every master zone.
    if (availableOffices.length === 0 && this.builderContext.corporateView) {
      this.businessService.findAllActiveOfficesAcrossMasterZones()
        .pipe(this.waiting.operator())
        .subscribe(
          (offices) => openBulkReassignDialog(offices),
          (err) => this.notificationService.processAll(err),
        );
      return;
    }

    openBulkReassignDialog(availableOffices);
  }

  /** Copy & archive is not offered for hidden leads or while “Hidden leads only” is enabled. */
  isCopyArchiveRowActionVisible(lead: LeadListItem): boolean {
    return !!lead?.activeSalesFolderId
      && !lead.isSuppressed
      && !this.form.get('showSuppressed')?.value;
  }

  isHideRowActionVisible(lead: LeadListItem): boolean {
    return !!lead?.activeSalesFolderId
      && !lead.isSuppressed
      && !this.form.get('showSuppressed')?.value;
  }

  isUnhideRowActionVisible(lead: LeadListItem): boolean {
    return !!lead?.activeSalesFolderId
      && (!!lead.isSuppressed || !!this.form.get('showSuppressed')?.value);
  }

  onRowHide(event: MouseEvent, lead: LeadListItem): void {
    event.stopPropagation();
    if (!this.isHideRowActionVisible(lead)) {
      return;
    }
    this.dialogService.confirmation({
      message: this.translation.get('msg_hide_lead_from_lists_confirmation'),
    }).subscribe((confirmed) => {
      if (confirmed !== Confirmation.Yes) {
        return;
      }
      this.businessService.bulkSuppress([{
        contactId: lead.contactId,
        salesFolderId: lead.activeSalesFolderId,
      }]).pipe(this.waiting.operator()).subscribe({
        next: () => {
          this.notificationService.success('msg_bulk_hide_leads_success');
          this.selectedLeads.delete(lead.contactId as any as number);
          this.cdr.markForCheck();
          this.onSearch();
        },
        error: (err) => this.notificationService.processAll(err),
      });
    });
  }

  onRowUnhide(event: MouseEvent, lead: LeadListItem): void {
    event.stopPropagation();
    if (!this.isUnhideRowActionVisible(lead)) {
      return;
    }
    this.dialogService.confirmation({
      message: this.translation.get('msg_unhide_lead_confirmation'),
    }).subscribe((confirmed) => {
      if (confirmed !== Confirmation.Yes) {
        return;
      }
      this.businessService.bulkUnsuppress([{
        contactId: lead.contactId,
        salesFolderId: lead.activeSalesFolderId,
      }]).pipe(this.waiting.operator()).subscribe({
        next: () => {
          this.notificationService.success('msg_bulk_unhide_leads_success');
          this.selectedLeads.delete(lead.contactId as any as number);
          this.cdr.markForCheck();
          this.onSearch();
        },
        error: (err) => this.notificationService.processAll(err),
      });
    });
  }

  onRowCopyArchive(event: MouseEvent, lead: LeadListItem): void {
    event.stopPropagation();
    this.openCopyArchiveForLead(lead);
  }

  openCopyArchiveForLead(lead: LeadListItem): void {
    if (!lead?.activeSalesFolderId) {
      return;
    }
    if (lead.isSuppressed || this.form.get('showSuppressed')?.value) {
      return;
    }

    const COMPLETED_STATUS_ID = 2;
    if (lead.meetingSfStatusId === COMPLETED_STATUS_ID) {
      this.notificationService.warning('msg_copy_archive_completed_lead');
      return;
    }

    const mzForm = this.form.get('masterZone')?.value;
    const masterZoneId =
      this.form.get('masterZoneId')?.value ||
      (mzForm?.id && mzForm.id !== '' && mzForm.id !== 'All' ? mzForm.id : null);

    this.dialogService.open(CopyArchiveLeadDialogComponent, {
      data: {
        leadId: String(lead.activeSalesFolderId),
        sourceOfficeId: lead.officeId != null ? String(lead.officeId) : undefined,
        masterZoneId: masterZoneId || undefined,
      },
      width: '560px',
      disableClose: true,
      autoFocus: false,
    }).afterClosed().subscribe((success) => {
      if (success) {
        this.selectedLeads.clear();
        this.cdr.markForCheck();
        this.onSearch();
      }
    });
  }
}